import { AsyncStatus, Status } from './../types/Async';
import { useCallback, useEffect, useRef, useState } from 'react';
import { requestErrorHandler } from '~/helpers/notifyError';
import { isValidError, log } from '~/helpers/bugsnagHelper';
import { AxiosError } from 'axios';
import { AxiosErrorType } from '~/helpers/orchestration/axiosWithAuthHandling';

type Input<T> = {
  fetcher: (args?: unknown) => Promise<T>;
  immediate?: boolean;
  fetcherArgs?: unknown[];
  refetchOpt?: { useRefetch?: boolean; maxAttempts?: number };
  logOpt?: { useLog?: boolean; logText?: string };
};

type State<T> = {
  status: Status;
  data: T | null;
  error: Error | AxiosError | null;
};

export const useAsync = function <T = unknown>(
  {
    fetcher,
    immediate = true,
    fetcherArgs = [],
    refetchOpt = {},
    logOpt = {}
  }: Input<T>,
  deps: unknown[] = []
) {
  const [state, setState] = useState<State<T>>({
    status: immediate ? AsyncStatus.pending : AsyncStatus.idle,
    data: null,
    error: null
  });

  const [currentExtraAttempt, setCurrentExtraAttempt] = useState(0);
  const { useRefetch = false, maxAttempts = 3 } = refetchOpt;
  const { useLog = false, logText } = logOpt;
  const execute = useRef(async (args: unknown[]) => {
    setState({
      status: AsyncStatus.pending,
      data: null,
      error: null
    });

    await executeFetcher(args);
  });

  const handleError = useCallback(
    (error: AxiosError) => {
      if (
        useRefetch &&
        currentExtraAttempt < maxAttempts &&
        error?.isAxiosError &&
        error?.response?.status === AxiosErrorType.CODE_503
      ) {
        setCurrentExtraAttempt(prevState => prevState + 1);
      } else {
        setState({
          status: AsyncStatus.error,
          data: null,
          error: isValidError(error) ? error : null
        });
      }
    },
    [currentExtraAttempt, useRefetch, maxAttempts]
  );

  const executeFetcher = useCallback(
    async (args: unknown[]) => {
      try {
        const response = await fetcher(...args);
        setState({
          status: AsyncStatus.success,
          data: response,
          error: null
        });

        currentExtraAttempt && setCurrentExtraAttempt(0);
        if (currentExtraAttempt && useLog) {
          log(new Error(logText), {
            severity: 'info' as const,
            currentExtraAttempt
          });
        }
      } catch (error: Error | unknown) {
        handleError(error as AxiosError);
      }
    },
    [fetcher, handleError, currentExtraAttempt, useLog, logText]
  );

  useEffect(() => {
    if (
      useRefetch &&
      currentExtraAttempt !== 0 &&
      state.status === AsyncStatus.pending &&
      currentExtraAttempt <= maxAttempts
    ) {
      const timer = setTimeout(() => {
        void executeFetcher(fetcherArgs);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [
    useRefetch,
    currentExtraAttempt,
    maxAttempts,
    executeFetcher,
    fetcherArgs,
    state.status
  ]);

  useEffect(() => {
    if (immediate) {
      execute.current(fetcherArgs).catch(requestErrorHandler);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediate, ...deps]);

  return {
    execute: execute.current,
    status: state.status,
    data: state.data,
    error: state.error
  };
};
